import { useRef, useState } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getExpandedRowModel,
  SortingState,
  ExpandedState,
} from '@tanstack/react-table';
import { Account } from '@/features/accounts/types';
import { TableBody, TableHead, TableRoot } from '@/shared/components/Table';
import { columns } from './AccountTable/columns';
import AccountRow from './AccountTable/AccountRow';
import { SortIndicator } from './AccountTable/ui';

interface Props {
  accounts: Account[];
  onActivated: () => void;
}

export default function AccountTable({ accounts, onActivated }: Props) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [expanded, setExpanded] = useState<ExpandedState>({});
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table is not compatible with React Compiler memoization
  const table = useReactTable({
    data: accounts,
    columns,
    state: {
      sorting,
      expanded,
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: () => true,
  });

  return (
    <TableRoot
      table={table}
      containerRef={tableContainerRef}
      containerClassName="max-h-[70vh] overflow-auto"
      tableClassName="min-w-[900px] table-fixed"
    >
      <colgroup>
        <col />
        <col style={{ width: 120 }} />
        <col style={{ width: 140 }} />
        <col style={{ width: 190 }} />
        <col style={{ width: 110 }} />
        <col style={{ width: 90 }} />
      </colgroup>
      <TableHead
        sticky
        headerCellClassNames={{
          delete: 'px-2',
          actions: 'whitespace-nowrap',
        }}
        SortIndicator={SortIndicator}
      />
      <TableBody<Account>>
        {(row) => (
          <AccountRow
            key={row.id}
            row={row}
            onActivated={onActivated}
          />
        )}
      </TableBody>
    </TableRoot>
  );
}
